import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { ChevronRight, ExternalLink, Printer } from 'lucide-react'
import { api } from '@/lib/api'
import { formatDate } from '@/lib/date'
import { useAuth } from '@/context/AuthContext'
import type { Job, Paginated } from '@/types'
import Card from '@/components/ui/Card'
import PageHeader from '@/components/ui/PageHeader'
import Button from '@/components/ui/Button'
import Pager from '@/components/ui/Pager'
import { Input } from '@/components/ui/Field'
import { Select } from '@/components/ui/Select'
import { StateBadge } from '@/components/ui/Badge'
import { Table, Row, Cell, EmptyRow, actionsColumn, actionsCellClass } from '@/components/ui/Table'
import { cn } from '@/lib/cn'
import UrgentBadge from '@/components/job/UrgentBadge'

type Tab = 'active' | 'history'

const TABS: { key: Tab; label: string }[] = [
  { key: 'active', label: 'Active' },
  { key: 'history', label: 'All jobs' },
]

const EMPTY: Record<Tab, string> = {
  active: 'No jobs assigned to you right now.',
  history: 'MPG Printing has not handed you a job yet.',
}

/**
 * The partner's job list: only jobs MPG has handed to this company. Active
 * is everything still in production; all jobs includes the completed ones.
 */
export default function PartnerJobs() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [tab, setTab] = useState<Tab>('active')
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('')
  const [page, setPage] = useState(1)

  const switchTab = (next: Tab) => {
    setTab(next)
    setStatus('')
    setPage(1)
  }

  const params: Record<string, string | number> = { scope: tab, page }
  if (search) params.search = search
  if (status) params.status = status

  const { data } = useQuery({
    queryKey: ['partner-jobs', tab, { search, status, page }],
    queryFn: async () => (await api.get<Paginated<Job>>('/partner/jobs', { params })).data,
  })

  const jobs = data?.data ?? []

  const statusOptions = [
    { value: 'queued', label: 'Queued' },
    { value: 'in_progress', label: 'In progress' },
    { value: 'issue', label: 'Issue' },
    ...(tab === 'active' ? [] : [{ value: 'completed', label: 'Completed' }]),
  ]

  const sectionName = (job: Job) =>
    job.current_department?.name ?? (job.status === 'completed' ? 'Done' : '-')

  return (
    <div>
      <PageHeader
        title="Jobs"
        subtitle={`${user?.partner?.name ?? 'Your company'} - jobs handed over by MPG Printing`}
      />

      <Card className="mb-5">
        <div className="flex flex-wrap items-end gap-4">
          <div className="-mx-1 flex max-w-full gap-1.5 overflow-x-auto px-1">
            {TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => switchTab(t.key)}
                className={cn(
                  'shrink-0 whitespace-nowrap rounded-lg border px-3.5 py-2 text-sm font-semibold',
                  tab === t.key
                    ? 'border-primary/60 bg-primary/20 text-primary'
                    : 'border-line text-muted hover:bg-foreground/5',
                )}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className="flex w-full items-end gap-3 sm:w-auto">
            <Input
              placeholder="Search job code..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value)
                setPage(1)
              }}
              className="min-w-0 flex-1 sm:w-48 sm:flex-none"
            />
            <Select
              value={status}
              onChange={(value) => {
                setStatus(value)
                setPage(1)
              }}
              options={statusOptions}
              placeholder="Any status"
              className="min-w-0 flex-1 sm:w-40 sm:flex-none"
            />
          </div>
        </div>
      </Card>

      {/* Phones get tappable cards, same as the staff list. */}
      <div className="space-y-3 sm:hidden">
        {jobs.length === 0 && (
          <div className="box px-4 py-10 text-center text-sm text-foreground/60">{EMPTY[tab]}</div>
        )}
        {jobs.map((job) => (
          <div
            key={job.id}
            onClick={() => navigate(`/partner/jobs/${job.qr_token}`)}
            className="box w-full cursor-pointer p-4 text-left transition-colors active:bg-foreground/5"
          >
            <div className="flex items-center justify-between gap-3">
              <span className="font-mono text-xs font-bold">{job.display_code ?? job.code}</span>
              <UrgentBadge urgent={job.order?.is_urgent} className="ml-1.5" />
              <StateBadge state={job.status} />
            </div>
            <div className="mt-2.5 flex items-center justify-between gap-3 text-sm">
              <span className="min-w-0">
                <span className="block truncate text-foreground">{job.order?.number}</span>
                <span className="mt-0.5 block text-xs text-foreground/60">
                  {job.order?.total_pieces} pcs - {sectionName(job)}
                </span>
                {job.current_department_due && (
                  <span className={cn('mt-0.5 block text-xs', job.late ? 'font-semibold text-status-danger' : 'text-foreground/60')}>
                    Due {formatDate(job.current_department_due)}
                  </span>
                )}
              </span>
              <span className="flex shrink-0 items-center gap-2">
                <button
                  type="button"
                  aria-label="Print job sheet"
                  onClick={(e) => {
                    e.stopPropagation()
                    window.open(`/partner/jobs/${job.id}/sheet`, '_blank', 'noopener')
                  }}
                  className="-m-2 p-2 text-foreground/40 transition-colors active:text-foreground"
                >
                  <Printer className="size-4" />
                </button>
                <ChevronRight className="size-4 text-foreground/40" />
              </span>
            </div>
          </div>
        ))}
      </div>

      <div className="hidden sm:block">
        <Table columns={['Job code', 'Order', 'Pieces', 'Handed over', 'Due', 'Status', 'Section', actionsColumn]}>
          {jobs.length === 0 && <EmptyRow colSpan={8}>{EMPTY[tab]}</EmptyRow>}
          {jobs.map((job) => (
            <Row key={job.id} onClick={() => navigate(`/partner/jobs/${job.qr_token}`)}>
              <Cell className="font-mono text-xs font-bold">
                {job.display_code ?? job.code}
                <UrgentBadge urgent={job.order?.is_urgent} className="ml-1.5" />
              </Cell>
              <Cell>{job.order?.number}</Cell>
              <Cell>{job.order?.total_pieces}</Cell>
              <Cell>{job.outsourced_at ? formatDate(job.outsourced_at) : '-'}</Cell>
              <Cell className={cn(job.late && 'font-semibold text-status-danger')}>
                {job.current_department_due ? formatDate(job.current_department_due) : '-'}
              </Cell>
              <Cell>
                <StateBadge state={job.status} />
              </Cell>
              <Cell>{sectionName(job)}</Cell>
              <Cell className={actionsCellClass}>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation()
                      navigate(`/partner/jobs/${job.qr_token}`)
                    }}
                  >
                    <ExternalLink className="size-4" /> Open
                  </Button>
                  <Button
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation()
                      window.open(`/partner/jobs/${job.id}/sheet`, '_blank', 'noopener')
                    }}
                  >
                    <Printer className="size-4" /> Sheet
                  </Button>
                </div>
              </Cell>
            </Row>
          ))}
        </Table>
      </div>

      <Pager meta={data?.meta} page={page} onPage={setPage} />
    </div>
  )
}
